import { Dialog, DialogHeader, DialogBody } from "../ui/Dialog";
import { cn } from "../../lib/cn";

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent);
const mod = isMac ? "⌘" : "Ctrl";
const shift = isMac ? "⇧" : "Shift";

interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string; description: string }[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "General",
    shortcuts: [
      { keys: `${mod}+S`, description: "Save and compile" },
      { keys: `${mod}+Enter`, description: "Compile" },
      { keys: `${mod}+Z`, description: "Undo" },
      { keys: `${mod}+${shift}+Z`, description: "Redo" },
      { keys: `${mod}+F`, description: "Find & replace" },
    ],
  },
  {
    title: "Formatting",
    shortcuts: [
      { keys: `${mod}+B`, description: "Bold" },
      { keys: `${mod}+I`, description: "Italic" },
      { keys: `${mod}+U`, description: "Underline" },
      { keys: `${mod}+/`, description: "Toggle comment" },
      { keys: `${mod}+${shift}+M`, description: "Inline math" },
    ],
  },
  {
    title: "AI",
    shortcuts: [
      { keys: `${mod}+G`, description: "Inline AI assistant" },
    ],
  },
  {
    title: "Navigation",
    shortcuts: [
      { keys: `${mod}+P`, description: "Quick open" },
      { keys: `${mod}+${shift}+O`, description: "Go to symbol" },
    ],
  },
  {
    title: "Editor",
    shortcuts: [
      { keys: `${mod}+D`, description: "Select next occurrence" },
      { keys: `${mod}+${shift}+K`, description: "Delete line" },
      { keys: `${mod}+${shift}+Enter`, description: "Insert line above" },
      { keys: `Tab`, description: "Indent" },
      { keys: `${shift}+Tab`, description: "Outdent" },
    ],
  },
];

function Kbd({ children }: { children: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-[4px] px-1.5",
        "text-[10px] font-medium font-mono leading-none",
        "bg-[var(--bg-surface-hover)] border border-[var(--border-subtle)]",
        "text-[var(--text-secondary)]",
        "shadow-[0_1px_0_var(--border-subtle)]",
      )}
    >
      {children}
    </kbd>
  );
}

export function KeyboardShortcutsDialog({ open, onClose }: KeyboardShortcutsDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} className="max-w-lg">
      <DialogHeader>
        <span className="text-[15px] font-semibold text-[var(--text-primary)]">
          Keyboard Shortcuts
        </span>
      </DialogHeader>

      <DialogBody className="max-h-[60vh] overflow-y-auto">
        <div className="grid grid-cols-2 gap-6">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-2">
                {group.title}
              </h3>
              <div className="space-y-1.5">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.description}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="text-[11px] text-[var(--text-secondary)]">
                      {shortcut.description}
                    </span>
                    <div className="flex items-center gap-0.5">
                      {shortcut.keys.split("+").map((key, i) => (
                        <Kbd key={i}>{key}</Kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogBody>
    </Dialog>
  );
}
